import { useRootStore } from '@qsrsoft/myq-core';
import { postAPI } from '@/helpers/reportService';

// Report-open tracking for the QSR Soft Admin "Report Usage" tab.
//
// The UI tells the API "this user opened report X (tab Y)" and nothing else;
// the API stamps the user, tenant and roles from the verified token and
// dedupes per 10-minute bucket. The same dedupe window is applied here so a
// user flipping between two reports does not post on every navigation. It is
// kept in localStorage so a reload inside the window does not post again
// either: the API turns a repeat away, but the attempt still costs a write.

export const REPORT_OPEN_URL = 'data_layer/mt/v1/usage/report-open';
export const DEDUPE_MS = 10 * 60 * 1000;
export const DASHBOARD_REPORT_ID = 'reports-mtDashboard';

export const SENT_STORAGE_KEY = 'reportUsage.sent';

let sent = null;

function defaultStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    return null;
  }
}

// Opens already posted, `<org>|<user>|<report>|<tab>` -> ms. Loaded once from
// storage; storage that is missing, blocked or corrupt just means in-memory.
function sentMap(storage) {
  if (sent) return sent;
  sent = new Map();
  try {
    const saved = JSON.parse(storage?.getItem(SENT_STORAGE_KEY) || '{}');
    Object.entries(saved).forEach(([key, at]) => {
      if (Number.isFinite(at)) sent.set(key, at);
    });
  } catch {
    // ignore: start empty
  }
  return sent;
}

// Writes back only the entries still inside the window, so it never grows.
function saveSent(storage, now) {
  try {
    const live = {};
    sent.forEach((at, key) => {
      if (now - at < DEDUPE_MS) live[key] = at;
    });
    storage?.setItem(SENT_STORAGE_KEY, JSON.stringify(live));
  } catch {
    // ignore: full or blocked storage only loses the cross-reload dedupe
  }
}

export const NAME_ID_PREFIX = 'name:';

/**
 * The usage id for a route: its meta.id, the dashboard's synthetic id, or
 * `name:<route name>` for the few routes that never got a meta.id, so no
 * report is invisible just because its route is older.
 */
export function reportIdForRoute(route) {
  if (!route) return null;
  if (route.meta?.id) return route.meta.id;
  if (route.name === 'Home') return DASHBOARD_REPORT_ID;
  if (typeof route.name === 'string' && route.name)
    return `${NAME_ID_PREFIX}${route.name}`;
  return null;
}

/**
 * Post one open, fire and forget. Returns true when a request was sent,
 * false when it was deduped or there is no org to attribute it to. Never
 * throws and never rejects: usage must not be able to break a report.
 */
export function trackReportOpen(
  reportId,
  tab = null,
  {
    now = Date.now(),
    post = postAPI,
    store = null,
    storage = defaultStorage(),
  } = {},
) {
  if (!reportId) return false;

  let orgId = null;
  let orgName = null;
  let userId = null;
  try {
    const root = store || useRootStore();
    orgId = root?.orgId || null;
    orgName = root?.orgName || null;
    userId = root?.userId || null;
  } catch {
    orgId = null;
  }
  if (!orgId) return false;

  // org and user are in the key because storage outlives a login
  const key = `${orgId}|${userId || ''}|${reportId}|${tab || ''}`;
  const seen = sentMap(storage);
  const last = seen.get(key);
  if (last !== undefined && now - last < DEDUPE_MS) return false;

  seen.set(key, now);
  saveSent(storage, now);
  const body = { orgId, reportId };
  if (orgName) body.orgName = String(orgName);
  if (tab) body.tab = String(tab);
  Promise.resolve()
    .then(() => post(REPORT_OPEN_URL, body))
    .catch(() => {
      // a failed usage post is nobody's problem but ours; retry on the next
      // open after the dedupe window
      seen.delete(key);
      saveSent(storage, now);
    });
  return true;
}

export function resetReportUsageTracking(storage = defaultStorage()) {
  sent = null;
  try {
    storage?.removeItem(SENT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

/** Record every report route landing as a report open. */
export function installReportUsageTracking(router, options = {}) {
  if (!router?.afterEach) return () => {};
  return router.afterEach((to) => {
    trackReportOpen(reportIdForRoute(to), null, options);
  });
}
